import React from "react";
import {
  Download,
  Eye,
  FileText,
  FolderKanban,
  History,
  LoaderCircle,
  ReceiptText,
  Search,
  WalletCards,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import "./OssInvoicesPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* =========================================================
   FORMATTERS
   ========================================================= */

function formatMoney(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const numeric = Number(value);

  if (Number.isNaN(numeric)) {
    return value;
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return `${formattedDate} ${formattedTime}`;
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatRenovationType(value = "") {
  if (!value) {
    return "—";
  }

  return String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/* =========================================================
   API MAPPING
   Supports both the previous camelCase responses and the
   new serializer-style snake_case response.
   ========================================================= */

function mapVersionFromApi(version = {}) {
  const versionNumber = version.versionNumber ?? version.version_number ?? 1;

  const uploadedAt = version.uploadedAt ?? version.uploaded_at ?? null;

  return {
    id: version.id,

    versionNumber,

    filename:
      version.filename ??
      version.originalFilename ??
      version.original_filename ??
      "",

    amount: version.amount ?? null,

    notes: version.notes ?? "",

    uploadedByName: version.uploadedByName ?? version.uploaded_by_name ?? "",

    uploadedAt,

    isLatest: Boolean(version.isLatest ?? version.is_latest),
  };
}

function mapInvoiceFromApi(invoice = {}) {
  const versions = Array.isArray(invoice.versions)
    ? invoice.versions
        .map(mapVersionFromApi)
        .sort((a, b) => b.versionNumber - a.versionNumber)
    : [];

  const latestVersionNumber =
    invoice.latestVersionNumber ??
    invoice.latest_version ??
    versions[0]?.versionNumber ??
    1;

  let latestVersion =
    versions.find((version) => version.versionNumber === latestVersionNumber) ||
    versions.find((version) => version.isLatest) ||
    versions[0] ||
    null;

  if (
    !latestVersion &&
    invoice.latestVersion &&
    typeof invoice.latestVersion === "object"
  ) {
    latestVersion = mapVersionFromApi(invoice.latestVersion);
  }

  const filename =
    invoice.latestOriginalFilename ??
    invoice.latest_original_filename ??
    invoice.filename ??
    latestVersion?.filename ??
    "";

  const amount =
    invoice.latestAmount ??
    invoice.latest_amount ??
    invoice.amount ??
    latestVersion?.amount ??
    null;

  const notes =
    invoice.latestNotes ?? invoice.latest_notes ?? latestVersion?.notes ?? "";

  const uploadedAt =
    invoice.latestUploadedAt ??
    invoice.latest_uploaded_at ??
    invoice.uploadedAt ??
    latestVersion?.uploadedAt ??
    null;

  const versionCount =
    invoice.versionCount ?? invoice.version_count ?? versions.length;

  return {
    id: invoice.id,

    renovationType: invoice.renovationType ?? invoice.renovation_type ?? "",

    renovationTypeLabel:
      invoice.renovationTypeLabel ??
      invoice.renovation_type_label ??
      formatRenovationType(invoice.renovationType ?? invoice.renovation_type),

    filename,

    amount,

    notes,

    uploadedAt,

    latestVersionNumber,

    versionCount,

    latestVersion,

    versions,

    createdAt: invoice.createdAt ?? invoice.created_at ?? null,

    updatedAt: invoice.updatedAt ?? invoice.updated_at ?? null,
  };
}

function mapFundingSourceFromApi(source = {}) {
  return {
    id: source.id,

    sourceTypeCode:
      source.sourceTypeCode ??
      source.source_type_code ??
      "",

    fundingType:
      source.fundingType ??
      source.funding_type ??
      source.category ??
      "",

    fundingTypeLabel:
      source.fundingTypeLabel ??
      source.funding_type_label ??
      source.name ??
      "",

    financingProduct:
      source.financingProduct ??
      source.financing_product ??
      "",

    providerName:
      source.providerName ??
      source.provider_name ??
      "",

    renovationAmount:
      source.renovationAmount ??
      source.renovation_amount ??
      0,

    allocatedAmount:
      source.allocatedAmount ??
      source.allocated_amount ??
      0,

    remainingAmount:
      source.remainingAmount ??
      source.remaining_amount ??
      0,

    status:
      source.status ??
      "",

    statusLabel:
      source.statusLabel ??
      source.status_label ??
      source.status ??
      "",
  };
}

function mapProjectSummary(data = {}) {
  const financial =
    data.financialSummary ??
    data.financial_summary ??
    {};

  const summary =
    data.summary ??
    {};

  const renovationBudget =
    financial.renovationBudget ??
    financial.renovation_budget ??
    financial.renovationAmount ??
    financial.renovation_amount ??
    data.totalRenovationAmount ??
    data.total_renovation_amount ??
    summary.renovationAmount ??
    summary.renovation_amount ??
    null;

  const totalProjectFunding =
    financial.totalProjectFunding ??
    financial.total_project_funding ??
    summary.totalProjectFunding ??
    summary.total_project_funding ??
    0;

  const confirmedFunding =
    financial.confirmedFunding ??
    financial.confirmed_funding ??
    0;

  const expectedFunding =
    financial.expectedFunding ??
    financial.expected_funding ??
    0;

  const totalFundingAllocated =
    financial.totalFundingAllocated ??
    financial.total_funding_allocated ??
    0;

  const totalFundingRemaining =
    financial.totalFundingRemaining ??
    financial.total_funding_remaining ??
    Math.max(
      Number(totalProjectFunding || 0) -
        Number(totalFundingAllocated || 0),
      0,
    );

  const totalInvoiced =
    financial.totalInvoiced ??
    financial.total_invoiced ??
    data.totalInvoicedAmount ??
    data.total_invoiced_amount ??
    summary.totalInvoiced ??
    summary.total_invoiced ??
    0;

  const projectBudgetRemaining =
    financial.projectBudgetRemaining ??
    financial.project_budget_remaining ??
    financial.remainingAmount ??
    financial.remaining_amount ??
    data.remainingRenovationAmount ??
    data.remaining_renovation_amount ??
    summary.remainingAmount ??
    summary.remaining_amount ??
    (renovationBudget !== null
      ? Math.max(
          Number(renovationBudget) -
            Number(totalInvoiced),
          0,
        )
      : null);

  const invoiceCount =
    financial.invoiceCount ??
    financial.invoice_count ??
    summary.invoiceCount ??
    summary.invoice_count ??
    summary.invoicesCount ??
    data.invoicesCount ??
    data.invoices_count ??
    0;

  const fundingSourceCount =
    financial.fundingSourceCount ??
    financial.funding_source_count ??
    0;

  return {
    renovationBudget,

    // Backward-compatible aliases.
    renovationAmount:
      renovationBudget,

    remainingAmount:
      projectBudgetRemaining,

    projectBudgetRemaining,

    totalProjectFunding,
    confirmedFunding,
    expectedFunding,

    totalFundingAllocated,
    totalFundingRemaining,

    totalInvoiced,
    invoiceCount,
    fundingSourceCount,
  };
}

function mapProjectFromApi(data = {}) {
  return {
    projectId: data.projectId ?? data.project_id ?? data.id,

    projectCode: data.projectCode ?? data.project_code ?? data.code ?? "",

    projectTitle: data.projectTitle ?? data.project_title ?? data.title ?? "",

    homeowner:
      data.homeowner ?? data.homeownerName ?? data.homeowner_name ?? "",

    country: data.country ?? "",

    status: data.statusLabel ?? data.status_label ?? data.status ?? "",

    lastUpdate:
      data.lastUpdate ??
      data.last_update ??
      data.updatedAt ??
      data.updated_at ??
      null,

    financialSummary: mapProjectSummary(data),

    fundingSources: Array.isArray(
      data.fundingSources ??
        data.funding_sources,
    )
      ? (
          data.fundingSources ??
          data.funding_sources
        ).map(mapFundingSourceFromApi)
      : [],

    invoices: Array.isArray(data.invoices)
      ? data.invoices.map(mapInvoiceFromApi)
      : [],
  };
}

/* =========================================================
   PROJECT SIDEBAR
   ========================================================= */

function ProjectsPanel({
  projects,
  selectedProjectId,
  search,
  onSearchChange,
  onSelect,
  loading,
  t,
}) {
  return (
    <section className="oss-invoices-projects">
      <div className="oss-invoices-panel-header">
        <div>
          <span className="oss-invoices-panel-kicker">
            <FolderKanban size={14} />
            {t("workspace.projects.kicker", "Projects")}
          </span>

          <h2>{t("workspace.projects.title", "Renovation projects")}</h2>
        </div>

        <span className="oss-invoices-count-pill">{projects.length}</span>
      </div>

      <label className="oss-invoices-search">
        <Search size={16} />

        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t(
            "workspace.projects.search",
            "Search project or homebuyer...",
          )}
        />
      </label>

      <div className="oss-invoices-project-list">
        {loading ? (
          <div className="oss-invoices-state">
            <LoaderCircle size={20} className="oss-invoices-spin" />
            <span>
              {t("workspace.projects.loading", "Loading projects...")}
            </span>
          </div>
        ) : projects.length === 0 ? (
          <div className="oss-invoices-state">
            <FolderKanban size={21} />

            <strong>
              {t("workspace.projects.emptyTitle", "No projects found")}
            </strong>

            <span>
              {t(
                "workspace.projects.emptyText",
                "No renovation projects match this search.",
              )}
            </span>
          </div>
        ) : (
          projects.map((project) => {
            const selected =
              String(project.projectId) === String(selectedProjectId);

            return (
              <button
                key={project.projectId}
                type="button"
                className={`oss-invoices-project-card ${
                  selected ? "is-selected" : ""
                }`}
                onClick={() => onSelect(project.projectId)}
              >
                <div className="oss-invoices-project-card__top">
                  <div>
                    <strong>
                      {project.projectCode || `#${project.projectId}`}
                    </strong>

                    <span>
                      {project.homeowner || t("common.homebuyer", "Homebuyer")}
                    </span>
                  </div>

                  <span className="oss-invoices-project-card__invoice-count">
                    {project.financialSummary?.invoiceCount ?? 0}
                  </span>
                </div>

                <div className="oss-invoices-project-card__money">
                  <div>
                    <span>{t("workspace.projects.invoiced", "Invoiced")}</span>

                    <strong>
                      {formatMoney(project.financialSummary?.totalInvoiced)}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t(
                        "workspace.projects.remaining",
                        "Funding remaining",
                      )}
                    </span>

                    <strong>
                      {formatMoney(
                        project.financialSummary?.totalFundingRemaining,
                      )}
                    </strong>
                  </div>
                </div>

                {project.lastUpdate ? (
                  <small>
                    {t("workspace.projects.updated", "Updated")}{" "}
                    {formatDate(project.lastUpdate)}
                  </small>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </section>
  );
}

/* =========================================================
   FINANCIAL SUMMARY
   ========================================================= */

function FinancialSummary({ project, t }) {
  if (!project) {
    return null;
  }

  const {
    renovationBudget,
    totalProjectFunding,
    confirmedFunding,
    expectedFunding,
    totalFundingAllocated,
    totalFundingRemaining,
    totalInvoiced,
    invoiceCount,
  } = project.financialSummary;

  const fundingSources = Array.isArray(
    project.fundingSources,
  )
    ? project.fundingSources.filter(
        (source) => {
          const status = String(
            source.status || "",
          )
            .trim()
            .toUpperCase();

          return (
            Number(
              source.renovationAmount || 0,
            ) > 0 &&
            ![
              "REJECTED",
              "CANCELLED",
            ].includes(status)
          );
        },
      )
    : [];

  const fundingTotal =
    Number(totalProjectFunding || 0);

  const allocated =
    Number(totalFundingAllocated || 0);

  const actualPercentage =
    fundingTotal > 0
      ? Math.round(
          (allocated / fundingTotal) *
            100,
        )
      : 0;

  const visualPercentage =
    Math.min(
      Math.max(
        actualPercentage,
        0,
      ),
      100,
    );

  return (
    <section className="oss-invoices-financial">
      <div className="oss-invoices-financial__heading">
        <div>
          <span>
            {t(
              "workspace.financial.kicker",
              "Project funding & expenditure",
            )}
          </span>

          <h2>
            {project.projectCode}
            {project.homeowner
              ? ` · ${project.homeowner}`
              : ""}
          </h2>
        </div>

        {project.status ? (
          <span className="oss-invoices-project-status">
            {project.status}
          </span>
        ) : null}
      </div>

      <div className="oss-invoices-financial__cards">
        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.totalFunding",
                "Total project funding",
              )}
            </small>

            <strong>
              {formatMoney(
                totalProjectFunding,
              )}
            </strong>
          </div>
        </div>

        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <ReceiptText size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.invoiced",
                "Total invoiced",
              )}
            </small>

            <strong>
              {formatMoney(
                totalInvoiced,
              )}
            </strong>
          </div>
        </div>

        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.fundingRemaining",
                "Funding remaining",
              )}
            </small>

            <strong>
              {formatMoney(
                totalFundingRemaining,
              )}
            </strong>
          </div>
        </div>

        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <FileText size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.invoices",
                "Invoices",
              )}
            </small>

            <strong>
              {invoiceCount ?? 0}
            </strong>
          </div>
        </div>
      </div>

      {fundingTotal > 0 ? (
        <div className="oss-invoices-budget-progress">
          <div className="oss-invoices-budget-progress__top">
            <span>
              {t(
                "workspace.financial.fundingAllocatedProgress",
                "Project funding allocated to invoices",
              )}
            </span>

            <strong>
              {actualPercentage}%
            </strong>
          </div>

          <div className="oss-invoices-budget-progress__track">
            <span
              style={{
                width: `${visualPercentage}%`,
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="oss-invoices-financial__cards">
        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.confirmedFunding",
                "Confirmed funding",
              )}
            </small>

            <strong>
              {formatMoney(
                confirmedFunding,
              )}
            </strong>
          </div>
        </div>

        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.expectedFunding",
                "Expected / applied funding",
              )}
            </small>

            <strong>
              {formatMoney(
                expectedFunding,
              )}
            </strong>
          </div>
        </div>

        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.renovationBudget",
                "Renovation budget",
              )}
            </small>

            <strong>
              {formatMoney(
                renovationBudget,
              )}
            </strong>
          </div>
        </div>

        <div className="oss-invoices-financial-card">
          <span className="oss-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>

          <div>
            <small>
              {t(
                "workspace.financial.allocated",
                "Funding allocated",
              )}
            </small>

            <strong>
              {formatMoney(
                totalFundingAllocated,
              )}
            </strong>
          </div>
        </div>
      </div>

      {fundingSources.length > 0 ? (
        <div className="oss-invoices-financial__cards">
          {fundingSources.map(
            (source) => (
              <div
                className="oss-invoices-financial-card"
                key={source.id}
              >
                <span className="oss-invoices-financial-card__icon">
                  <WalletCards size={18} />
                </span>

                <div>
                  <small>
                    {source.financingProduct ||
                      source.fundingTypeLabel ||
                      source.fundingType ||
                      t(
                        "workspace.financial.fundingSource",
                        "Funding source",
                      )}
                  </small>

                  <strong>
                    {formatMoney(
                      source.renovationAmount,
                    )}
                  </strong>

                  {source.providerName ? (
                    <small>
                      {source.providerName}
                    </small>
                  ) : null}

                  {source.statusLabel ? (
                    <small>
                      {source.statusLabel}
                    </small>
                  ) : null}
                </div>
              </div>
            ),
          )}
        </div>
      ) : null}
    </section>
  );
}

/* =========================================================
   INVOICE LIST
   ========================================================= */

function InvoiceList({ invoices, selectedInvoiceId, onSelect, loading, t }) {
  const [search, setSearch] = React.useState("");

  const filteredInvoices = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return invoices;
    }

    return invoices.filter((invoice) =>
      [
        invoice.filename,
        invoice.renovationTypeLabel,
        invoice.amount,
        `v${invoice.latestVersionNumber}`,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [invoices, search]);

  return (
    <section className="oss-invoices-list-panel">
      <div className="oss-invoices-panel-header">
        <div>
          <span className="oss-invoices-panel-kicker">
            <ReceiptText size={14} />
            {t("workspace.invoices.kicker", "Invoices")}
          </span>

          <h2>{t("workspace.invoices.title", "Project invoices")}</h2>
        </div>

        <span className="oss-invoices-count-pill">{invoices.length}</span>
      </div>

      <label className="oss-invoices-search">
        <Search size={16} />

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t(
            "workspace.invoices.search",
            "Search invoice, type or amount...",
          )}
          disabled={loading}
        />
      </label>

      <div className="oss-invoices-list-content">
        {filteredInvoices.length === 0 ? (
          <div className="oss-invoices-state oss-invoices-state--large">
            <ReceiptText size={24} />

            <strong>
              {t("workspace.invoices.emptyTitle", "No invoices found")}
            </strong>

            <span>
              {t(
                "workspace.invoices.emptyText",
                "Invoices uploaded for this project will appear here.",
              )}
            </span>
          </div>
        ) : (
          <div className="oss-invoices-table">
            <div className="oss-invoices-table__head">
              <span>{t("workspace.invoices.columns.file", "File")}</span>

              <span>{t("workspace.invoices.columns.type", "Renovation")}</span>

              <span>{t("workspace.invoices.columns.amount", "Amount")}</span>

              <span>{t("workspace.invoices.columns.version", "Version")}</span>
            </div>

            <div className="oss-invoices-table__body">
              {filteredInvoices.map((invoice) => {
                const selected =
                  String(invoice.id) === String(selectedInvoiceId);

                return (
                  <button
                    key={invoice.id}
                    type="button"
                    className={`oss-invoices-table-row ${
                      selected ? "is-selected" : ""
                    }`}
                    onClick={() => onSelect(invoice.id)}
                    disabled={loading}
                  >
                    <div className="oss-invoices-table-row__file">
                      <span className="oss-invoices-mobile-label">
                        {t("workspace.invoices.columns.file", "File")}
                      </span>

                      <strong>
                        {invoice.filename ||
                          t("workspace.invoices.defaultFilename", "Invoice")}
                      </strong>

                      <small>{formatDateTime(invoice.uploadedAt)}</small>
                    </div>

                    <div>
                      <span className="oss-invoices-mobile-label">
                        {t("workspace.invoices.columns.type", "Renovation")}
                      </span>

                      <span>{invoice.renovationTypeLabel}</span>
                    </div>

                    <div className="oss-invoices-table-row__amount">
                      <span className="oss-invoices-mobile-label">
                        {t("workspace.invoices.columns.amount", "Amount")}
                      </span>

                      <strong>{formatMoney(invoice.amount)}</strong>
                    </div>

                    <div>
                      <span className="oss-invoices-mobile-label">
                        {t("workspace.invoices.columns.version", "Version")}
                      </span>

                      <div className="oss-invoices-version-cell">
                        <span>v{invoice.latestVersionNumber}</span>

                        {invoice.versionCount > 1 ? (
                          <span className="oss-invoices-updated-badge">
                            {t("workspace.invoices.updated", "Updated")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="oss-invoices-content-loader">
            <div className="oss-invoices-content-loader__inner">
              <LoaderCircle size={27} className="oss-invoices-spin" />

              <strong>
                {t(
                  "workspace.projectLoading.title",
                  "Loading project invoices",
                )}
              </strong>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* =========================================================
   VERSION HISTORY
   ========================================================= */

function VersionHistory({ invoice, onViewVersion, onDownloadVersion, t }) {
  if (!invoice?.versions?.length) {
    return null;
  }

  return (
    <div className="oss-invoice-details__versions">
      {invoice.versions.map((version, index) => {
        const isLatest = version.versionNumber === invoice.latestVersionNumber;

        return (
          <div
            key={version.id ?? `${invoice.id}-${version.versionNumber}`}
            className={`oss-invoice-version ${isLatest ? "is-latest" : ""}`}
          >
            <div className="oss-invoice-version__top">
              <div>
                <strong>v{version.versionNumber}</strong>

                {isLatest ? (
                  <span>{t("workspace.details.latest", "Latest")}</span>
                ) : null}
              </div>

              <small>{formatDateTime(version.uploadedAt)}</small>
            </div>

            <p>{version.filename || "—"}</p>

            <div className="oss-invoice-version__meta">
              <strong>{formatMoney(version.amount)}</strong>

              {version.uploadedByName ? (
                <span>{version.uploadedByName}</span>
              ) : null}
            </div>

            {version.notes ? (
              <small className="oss-invoice-version__note">
                {version.notes}
              </small>
            ) : null}

            <div className="oss-invoice-version__actions">
              <button
                type="button"
                onClick={() => onViewVersion(version, invoice)}
              >
                <Eye size={14} />
                {t("workspace.actions.view", "View")}
              </button>

              <button
                type="button"
                onClick={() => onDownloadVersion(version, invoice)}
              >
                <Download size={14} />
                {t("workspace.actions.download", "Download")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* =========================================================
   INVOICE DETAIL
   ========================================================= */

function InvoiceDetails({
  invoice,
  loading,
  onViewVersion,
  onDownloadVersion,
  t,
}) {
  if (!invoice) {
    return (
      <aside className="oss-invoice-details oss-invoice-details--empty">
        <FileText size={28} />

        <h3>{t("workspace.details.emptyTitle", "Select an invoice")}</h3>

        <p>
          {t(
            "workspace.details.emptyText",
            "Choose an invoice to view its details and version history.",
          )}
        </p>

        {loading ? (
          <div className="oss-invoices-content-loader">
            <div className="oss-invoices-content-loader__inner">
              <LoaderCircle size={27} className="oss-invoices-spin" />

              <strong>
                {t("workspace.details.loading", "Loading invoice details")}
              </strong>
            </div>
          </div>
        ) : null}
      </aside>
    );
  }

  return (
    <aside className="oss-invoice-details">
      <div className="oss-invoice-details__header">
        <div className="oss-invoice-details__file-icon">
          <FileText size={20} />
        </div>

        <div>
          <span>{t("workspace.details.kicker", "Invoice details")}</span>

          <h3>
            {invoice.filename ||
              t("workspace.invoices.defaultFilename", "Invoice")}
          </h3>

          <p>{invoice.renovationTypeLabel}</p>
        </div>
      </div>

      <div className="oss-invoice-details__amount">
        <span>{t("workspace.details.amount", "Current amount")}</span>

        <strong>{formatMoney(invoice.amount)}</strong>
      </div>

      <div className="oss-invoice-details__facts">
        <div>
          <span>{t("workspace.details.version", "Current version")}</span>

          <strong>v{invoice.latestVersionNumber}</strong>
        </div>

        <div>
          <span>{t("workspace.details.uploaded", "Uploaded")}</span>

          <strong>{formatDateTime(invoice.uploadedAt)}</strong>
        </div>

        <div>
          <span>{t("workspace.details.versions", "Versions")}</span>

          <strong>{invoice.versionCount}</strong>
        </div>
      </div>

      {invoice.notes ? (
        <div className="oss-invoice-details__note">
          <span>{t("workspace.details.note", "Homebuyer note")}</span>

          <p>{invoice.notes}</p>
        </div>
      ) : null}

      {invoice.latestVersion ? (
        <div className="oss-invoice-details__primary-actions">
          <button
            type="button"
            className="primary"
            onClick={() => onViewVersion(invoice.latestVersion, invoice)}
          >
            <Eye size={15} />
            {t("workspace.actions.viewInvoice", "View invoice")}
          </button>

          <button
            type="button"
            className="secondary"
            onClick={() => onDownloadVersion(invoice.latestVersion, invoice)}
          >
            <Download size={15} />
            {t("workspace.actions.download", "Download")}
          </button>
        </div>
      ) : null}

      <div className="oss-invoice-details__history">
        <div className="oss-invoice-details__section-title">
          <History size={15} />

          <h4>{t("workspace.details.history", "Version history")}</h4>
        </div>

        <VersionHistory
          invoice={invoice}
          onViewVersion={onViewVersion}
          onDownloadVersion={onDownloadVersion}
          t={t}
        />
      </div>
    </aside>
  );
}

/* =========================================================
   PREVIEW MODAL
   ========================================================= */

function InvoicePreviewModal({ open, title, fileUrl, onClose, t }) {
  if (!open) {
    return null;
  }

  return (
    <div className="oss-invoice-preview" onClick={onClose}>
      <div
        className="oss-invoice-preview__dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="oss-invoice-preview__header">
          <div>
            <span>{t("workspace.preview.kicker", "Invoice preview")}</span>

            <h3>{title}</h3>
          </div>

          <button type="button" onClick={onClose}>
            {t("workspace.preview.close", "Close")}
          </button>
        </div>

        <div className="oss-invoice-preview__body">
          {fileUrl ? (
            <iframe src={fileUrl} title={title} />
          ) : (
            <div className="oss-invoices-state">
              {t("workspace.preview.unavailable", "Preview is not available.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PAGE
   ========================================================= */

export default function OssInvoicesPage() {
  const { t } = useTranslation("oss-invoices");

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth0();

  const [projects, setProjects] = React.useState([]);

  const [selectedProjectId, setSelectedProjectId] = React.useState(
    searchParams.get("project"),
  );

  const [project, setProject] = React.useState(null);

  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState(
    searchParams.get("invoice"),
  );

  const [projectSearchInput, setProjectSearchInput] = React.useState("");

  const [projectSearch, setProjectSearch] = React.useState("");

  const [projectsLoading, setProjectsLoading] = React.useState(true);

  const [projectLoading, setProjectLoading] = React.useState(false);

  const [error, setError] = React.useState("");

  const [previewOpen, setPreviewOpen] = React.useState(false);

  const [previewUrl, setPreviewUrl] = React.useState("");

  const [previewTitle, setPreviewTitle] = React.useState("");

  /* -------------------------------------------------------
     Debounced project search
     ------------------------------------------------------- */

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setProjectSearch(projectSearchInput.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [projectSearchInput]);

  /* -------------------------------------------------------
     Load project list
     ------------------------------------------------------- */

  const loadProjects = React.useCallback(async () => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    try {
      setProjectsLoading(true);
      setError("");

      const token = await getAccessTokenSilently();

      const params = new URLSearchParams();

      if (projectSearch) {
        params.set("search", projectSearch);
      }

      const query = params.toString();

      const response = await fetch(
        `${API_BASE}/api/oss/invoice-projects/${query ? `?${query}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.detail || "Could not load invoice projects.");
      }

      const rawProjects = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : [];

      const mapped = rawProjects.map(mapProjectFromApi);

      setProjects(mapped);

      setSelectedProjectId((current) => {
        if (
          current &&
          mapped.some((item) => String(item.projectId) === String(current))
        ) {
          return current;
        }

        return mapped[0]?.projectId ?? null;
      });
    } catch (err) {
      console.error("Failed to load OSS invoice projects:", err);

      setProjects([]);
      setProject(null);

      setError(err?.message || "Could not load invoice projects.");
    } finally {
      setProjectsLoading(false);
    }
  }, [authLoading, isAuthenticated, getAccessTokenSilently, projectSearch]);

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  /* -------------------------------------------------------
     Load selected project + invoices
     ------------------------------------------------------- */

  const loadProject = React.useCallback(async () => {
    if (!isAuthenticated || !selectedProjectId) {
      setProject(null);
      return;
    }

    try {
      setProjectLoading(true);
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/oss/invoice-projects/${selectedProjectId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.detail || "Could not load project invoices.");
      }

      const mappedProject = mapProjectFromApi(data);

      setProject(mappedProject);

      setSelectedInvoiceId((current) => {
        if (
          current &&
          mappedProject.invoices.some(
            (invoice) => String(invoice.id) === String(current),
          )
        ) {
          return current;
        }

        return mappedProject.invoices[0]?.id ?? null;
      });
    } catch (err) {
      console.error("Failed to load invoice project:", err);

      setProject(null);

      setError(err?.message || "Could not load project invoices.");
    } finally {
      setProjectLoading(false);
    }
  }, [selectedProjectId, isAuthenticated, getAccessTokenSilently]);

  React.useEffect(() => {
    loadProject();
  }, [loadProject]);

  /* -------------------------------------------------------
     Keep URL query parameters in sync
     ------------------------------------------------------- */

  React.useEffect(() => {
    const params = new URLSearchParams();

    if (selectedProjectId) {
      params.set("project", selectedProjectId);
    }

    if (selectedInvoiceId) {
      params.set("invoice", selectedInvoiceId);
    }

    setSearchParams(params, {
      replace: true,
    });
  }, [selectedProjectId, selectedInvoiceId, setSearchParams]);

  const selectedInvoice =
    project?.invoices?.find(
      (invoice) => String(invoice.id) === String(selectedInvoiceId),
    ) ?? null;

  const selectedProjectLoaded =
    project &&
    selectedProjectId &&
    String(project.projectId) === String(selectedProjectId);

  const workspaceLoading =
    projectsLoading ||
    Boolean(selectedProjectId && (projectLoading || !selectedProjectLoaded));

  /* -------------------------------------------------------
     Project selection
     ------------------------------------------------------- */

  function handleSelectProject(projectId) {
    setSelectedProjectId(projectId);

    setSelectedInvoiceId(null);
  }

  /* -------------------------------------------------------
     Invoice file endpoint
     ------------------------------------------------------- */

  function getVersionFileUrl(version) {
    if (!version?.id) {
      return "";
    }

    return `${API_BASE}/api/oss/invoice-versions/${version.id}/file/`;
  }

  /* -------------------------------------------------------
     Preview
     ------------------------------------------------------- */

  async function handleViewVersion(version, invoice) {
    const fileUrl = getVersionFileUrl(version);

    if (!fileUrl) {
      return;
    }

    try {
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(fileUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Could not load invoice preview.");
      }

      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);

      setPreviewUrl((previous) => {
        if (previous) {
          URL.revokeObjectURL(previous);
        }

        return blobUrl;
      });

      setPreviewTitle(version?.filename || invoice?.filename || "Invoice");

      setPreviewOpen(true);
    } catch (err) {
      console.error(err);

      setError(err?.message || "Could not load invoice preview.");
    }
  }

  /* -------------------------------------------------------
     Download
     ------------------------------------------------------- */

  async function handleDownloadVersion(version, invoice) {
    const fileUrl = getVersionFileUrl(version);

    if (!fileUrl) {
      return;
    }

    try {
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(fileUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Could not download invoice.");
      }

      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");

      anchor.href = blobUrl;

      anchor.download = version?.filename || invoice?.filename || "invoice.pdf";

      document.body.appendChild(anchor);

      anchor.click();
      anchor.remove();

      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error(err);

      setError(err?.message || "Could not download invoice.");
    }
  }

  function closePreview() {
    setPreviewOpen(false);

    setPreviewUrl((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }

      return "";
    });
  }

  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /* -------------------------------------------------------
     AUTH / INITIAL LOADING
     ------------------------------------------------------- */

  if (authLoading) {
    return (
      <div className="oss-invoices-page">
        <div className="oss-invoices-page-loading">
          <LoaderCircle size={26} className="oss-invoices-spin" />

          {t("workspace.loading", "Loading invoices...")}
        </div>
      </div>
    );
  }

  return (
    <div className="oss-invoices-page">
      <header className="oss-invoices-hero">
        <div>
          <span className="oss-invoices-hero__eyebrow">
            {t("workspace.eyebrow", "Renovation management")}
          </span>

          <h1>{t("workspace.title", "Renovation invoices")}</h1>

          <p>
            {t(
              "workspace.subtitle",
              "Track invoices, renovation expenditure and remaining project funds.",
            )}
          </p>
        </div>
      </header>

      {error ? <div className="oss-invoices-error">{error}</div> : null}

      <FinancialSummary project={project} t={t} />

      <div className="oss-invoices-workspace">
        <ProjectsPanel
          projects={projects}
          selectedProjectId={selectedProjectId}
          search={projectSearchInput}
          onSearchChange={setProjectSearchInput}
          onSelect={handleSelectProject}
          loading={projectsLoading}
          t={t}
        />

        <InvoiceList
          invoices={selectedProjectLoaded ? (project?.invoices ?? []) : []}
          selectedInvoiceId={selectedInvoiceId}
          onSelect={setSelectedInvoiceId}
          loading={workspaceLoading}
          t={t}
        />

        <InvoiceDetails
          invoice={selectedProjectLoaded ? selectedInvoice : null}
          loading={workspaceLoading}
          onViewVersion={handleViewVersion}
          onDownloadVersion={handleDownloadVersion}
          t={t}
        />
      </div>

      <InvoicePreviewModal
        open={previewOpen}
        title={previewTitle}
        fileUrl={previewUrl}
        onClose={closePreview}
        t={t}
      />
    </div>
  );
}